<script setup lang="ts">
import { computed } from 'vue';
import ColumnMapping from '@/components/ColumnMapping.vue';
import {
    barPercent,
    formatDuration,
    formatGroupedNumber,
    formatHourOfDay,
    formatSizeOrRate,
    numericValueOf,
} from '@/components/record/fields/formatFieldValue';
import type { FieldKind } from '@/components/record/fields/fieldKind';
import type { TableColumnStaticProps } from '@/types/tableTypes';

/**
 * Every kind in the number family, with tabular figures:
 *
 * - a number and a count are grouped: "1,204";
 * - a size or rate has its unit: "12 GB", "192 kbps";
 * - a percentage and a volume have a thin bar out of 100;
 * - an hour of the day reads "11 PM", and a number that cannot be an hour
 *   shows raw, in red;
 * - a duration in seconds reads "3:01".
 *
 * IDs show as they do in tables: a record ID and a foreign key link to the
 * admin app, and a lookup ID shows its name.
 */
const props = defineProps<{
    kind: FieldKind;
    entityId: string;
    column: TableColumnStaticProps;
    row: Record<string, unknown>;
    value: unknown;
}>();

const isId = computed(() => props.kind === 'recordId' || props.kind === 'foreignKeyId' || props.kind === 'lookupId');
const numericValue = computed(() => numericValueOf(props.value));

const hourText = computed(() => (props.kind === 'hourOfDay' && numericValue.value !== null ? formatHourOfDay(numericValue.value) : null));
/** An hour field holding a number no hour can be, such as 5748718. */
const isImpossibleHour = computed(() => props.kind === 'hourOfDay' && hourText.value === null);

const text = computed(() => {
    const numberValue = numericValue.value;
    if (numberValue === null) {
        return String(props.value);
    }
    switch (props.kind) {
        case 'hourOfDay':
            return hourText.value ?? String(numberValue);
        case 'duration':
            return formatDuration(numberValue);
        case 'sizeOrRate':
            return formatSizeOrRate(props.column.id, numberValue);
        case 'percentage':
            return `${formatGroupedNumber(numberValue)}%`;
        default:
            return formatGroupedNumber(numberValue);
    }
});

const bar = computed(() =>
    (props.kind === 'percentage' || props.kind === 'volume') && numericValue.value !== null ? barPercent(numericValue.value) : null,
);
</script>

<template>
  <ColumnMapping v-if="isId" :field="column.key" :data-type="column.dataType" :data="row" :effective-entity-id="entityId" />
  <span v-else class="inline-flex items-center gap-2">
    <span
      data-testid="number-value"
      class="tabular-nums"
      :class="isImpossibleHour && 'text-red-700 dark:text-red-300'"
      :title="isImpossibleHour ? 'Not an hour of the day, which runs from 0 to 23' : undefined"
    >{{ text }}</span>
    <span v-if="bar !== null" class="h-1 w-16 shrink-0 overflow-hidden rounded-full bg-muted" aria-hidden="true">
      <span data-testid="value-bar" class="block h-full rounded-full bg-foreground/50" :style="{ width: `${bar}%` }" />
    </span>
  </span>
</template>
